// /en/thai-visa/$destination/$type — English A5 leaf (mirror of the Thai leaf)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  OUTBOUND_DESTINATIONS, OUTBOUND_VISA_TYPES,
  getOutboundDestination, getOutboundVisaType,
} from "@/data/thai-outbound-visas";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle, toEnglishLabel } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Plane, MessageCircle, Phone, BadgeCheck, MapPin, Clock, ShieldCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/thai-visa/$destination/$type")({
  loader: ({ params }) => {
    const d = getOutboundDestination(params.destination);
    const v = getOutboundVisaType(params.type);
    if (!d || !v) throw notFound();
    return { destination: params.destination, type: params.type };
  },
  head: ({ params }) => {
    const d = getOutboundDestination(params.destination);
    const v = getOutboundVisaType(params.type);
    if (!d || !v) return { meta: [{ title: "Not found | NYC Language Institute" }] };
    const url = `${SITE}/en/thai-visa/${d.slug}/${v.slug}`;
    const turnaround = toEnglishLabel(v.turnaround);
    const processing = toEnglishLabel(d.processingDays);
    const title = `${v.nameEn} for ${d.nameEn} from Thailand · Certified Translation + ${d.hague ? "Apostille" : "Embassy Legalisation"} | NYC`;
    const desc = `Document service for Thai nationals applying for a ${v.nameEn} to ${d.nameEn} — certified translation, MFA legalisation, ${d.hague ? "Hague Apostille" : `${d.embassyEn} legalisation`}, and pre-lodgement checking at ${d.vfsPartner || d.embassyEn}. Document prep ${turnaround}; embassy processing ${processing}.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/thai-visa/$destination/$type", "en") },
        { name: "description", content: ctaDesc(desc, "/en/thai-visa/$destination/$type", "en") },
        { name: "keywords", content: `${v.nameEn} ${d.nameEn}, ${d.nameEn} visa from Thailand, certified translation ${d.nameEn} visa, apostille ${d.nameEn}, ${d.embassyEn}` },
        { property: "og:title", content: ctaTitle(title, "/en/thai-visa/$destination/$type", "en") },
        { property: "og:description", content: ctaDesc(desc, "/en/thai-visa/$destination/$type", "en") },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/thai-visa/${d.slug}/${v.slug}`, `/en/thai-visa/${d.slug}/${v.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          { type: "application/ld+json" as const, children: JSON.stringify({
            "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
            name: `${v.nameEn} document preparation for ${d.nameEn}`,
            serviceType: `Thai outbound ${v.labelEn.toLowerCase()} visa document service`,
            areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: d.nameEn }],
            provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Language Institute", telephone: "+66832494999", url: SITE },
            offers: { "@type": "Offer", price: d.visaFeeThb + v.bundleThb, priceCurrency: "THB", availability: "https://schema.org/InStock" },
            aggregateRating: aggregateRatingNode(url),
          }) },
          { type: "application/ld+json" as const, children: JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
            mainEntity: [
              { q: `What does this ${v.nameEn} service cover?`, a: `${v.taglineEn} Destination ${d.nameEn} — ${d.hague ? "including Hague Apostille" : `including ${d.embassyEn} legalisation`}, prepared as a complete file.` },
              { q: `Is ${d.nameEn} a Hague Apostille country?`, a: d.hague ? `Yes — an Apostille issued by MFA Thailand is accepted directly, with no embassy legalisation step.` : `No — documents need MFA legalisation plus a further legalisation at ${d.embassyEn}.` },
              { q: `How long does document preparation take?`, a: `${turnaround} for the document set, followed by roughly ${processing} of embassy processing.` },
              { q: `How are the documents translated?`, a: v.translationEn },
              { q: `Is a ${d.vfsPartner || "embassy"} appointment required?`, a: d.bioRequired ? `Yes — ${d.vfsPartner || d.embassyEn} requires a biometrics appointment booked in advance.` : `No biometrics appointment is required; the file is lodged directly at ${d.embassyEn}.` },
            ].map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }) },
          { type: "application/ld+json" as const, children: JSON.stringify({
            "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
              { "@type": "ListItem", position: 2, name: "Visas for Thai nationals", item: `${SITE}/en/thai-visa` },
              { "@type": "ListItem", position: 3, name: d.nameEn, item: `${SITE}/en/thai-visa/${d.slug}` },
              { "@type": "ListItem", position: 4, name: v.nameEn, item: url },
            ],
          }) },
        ]),
      ],
    };
  },
  component: EnOutboundLeaf,
});

function EnOutboundLeaf() {
  const { destination, type } = Route.useLoaderData() as { destination: string; type: string };
  const d = getOutboundDestination(destination)!;
  const v = getOutboundVisaType(type)!;
  const turnaround = toEnglishLabel(v.turnaround);
  const processing = toEnglishLabel(d.processingDays);
  const hours = toEnglishLabel(d.embassyHours);
  const related = OUTBOUND_VISA_TYPES.filter((x) => x.slug !== v.slug);
  const otherDest = OUTBOUND_DESTINATIONS.filter((x) => x.slug !== d.slug).slice(0, 9);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/en/thai-visa" className="hover:text-gold">Visas for Thai nationals</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{d.nameEn} · {v.labelEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> {d.regionEn} · {v.labelEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {v.nameEn} for Thai nationals travelling to <span className="text-gold">{d.nameEn}</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {v.taglineEn} · {d.hague ? "Hague Apostille route" : `MFA + ${d.embassyEn}`} · document prep {turnaround}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md"><MessageCircle className="h-4 w-4" /> LINE @nycli</a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md"><Phone className="h-4 w-4" /> +66 83 249 4999</a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800"><BadgeCheck className="h-3 w-3" /> Quotable</span>
          {v.nameEn} to {d.nameEn} for Thai nationals: a complete document set with certified translation plus {d.hague ? "a Hague Apostille" : `${d.embassyEn} legalisation`}. Document preparation takes {turnaround}; embassy processing runs about {processing}.
        </blockquote>

        <section className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: MapPin, label: "Embassy", value: d.embassyEn, sub: d.embassyAddr },
            { icon: Clock, label: "Embassy processing", value: processing, sub: hours },
            { icon: BadgeCheck, label: "Document prep", value: turnaround, sub: v.labelEn },
            { icon: ShieldCheck, label: "Legalisation route", value: d.hague ? "Apostille (Hague)" : "MFA + embassy", sub: d.vfsPartner ? `Lodged via ${d.vfsPartner}` : "Lodged directly at the embassy" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
              <s.icon className="h-5 w-5 text-amber-600" />
              <div className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">{s.label}</div>
              <div className="mt-1 text-sm font-bold text-slate-900">{s.value}</div>
              <div className="mt-1 text-[11px] leading-relaxed text-slate-600">{s.sub}</div>
            </div>
          ))}
        </section>

        <section className="mt-10 rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
          <h2 className="font-display text-lg font-extrabold text-navy-deep sm:text-xl">Why {d.nameEn}?</h2>
          <p className="mt-2 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">{d.hallmarkEn}</p>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Documents to prepare</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {v.coreDocsEn.map((c, i) => (
              <li key={i} className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 text-[14px] text-slate-800">
                <BadgeCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-2xl border-l-4 border-amber-500 bg-amber-50/50 p-4 text-[13px] leading-relaxed text-slate-800">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{d.nameEn}-specific pitfall</div>
            <p className="mt-1">{d.quirkEn}</p>
          </div>
        </section>

        <section className="mt-10 rounded-2xl bg-slate-50 p-5">
          <h2 className="font-display text-lg font-extrabold text-navy-deep sm:text-xl">Translation and legalisation policy</h2>
          <p className="mt-2 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">{v.translationEn}</p>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions</h2>
          <div className="mt-5">
            <FaqAccordion items={[
              { q: `Is ${d.nameEn} a Hague Apostille country?`, a: d.hague ? `Yes — an Apostille from MFA Thailand is accepted directly.` : `No — documents need MFA legalisation plus ${d.embassyEn} legalisation.` },
              { q: `Is a NAATI translation required?`, a: d.slug === "australia" ? "Yes — Australian authorities accept NAATI-certified translations only." : d.slug === "new-zealand" ? "Both NAATI-certified and MFA-legalised translations are accepted." : "No — MFA-legalised certified translation is sufficient." },
              { q: `What is the total timeline?`, a: `Document preparation ${turnaround}, then embassy processing of roughly ${processing}.` },
              { q: `Who books the ${d.vfsPartner || "embassy"} appointment?`, a: `We book it through ${d.vfsPartner || d.embassyEn} and hand over a checked file ready for the appointment date.` },
              { q: `Does NYC guarantee approval?`, a: `No. The embassy alone decides. We guarantee only that your documents, translations and legalisations are correct and accepted at intake.` },
            ]} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other {d.nameEn} visa categories</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/en/thai-visa/$destination/$type" params={{ destination: d.slug, type: r.slug }} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <span className="font-semibold text-slate-800">{r.nameEn}</span>
                <span className="text-[11px] text-slate-500">{r.labelEn}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other popular destinations</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherDest.map((o) => (
              <Link key={o.slug} to="/en/thai-visa/$destination/$type" params={{ destination: o.slug, type: v.slug }} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <span className="font-semibold text-slate-800">{v.labelEn} · {o.nameEn}</span>
                <span className="text-[11px] text-slate-500">{o.hague ? "Apostille" : "Embassy"}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
